<template>
    <base-dialog :loading="loading" :value="dialog" :width="760" :fullscreen="isMobile" :title="title"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="hc-body">
            <div>
                <kimsa-text-field :label="$t('habits.combo_name')" :value="form.name" @change="form.name = $event"
                    :placeholder="$t('common.write_here')" dense force-value />
                <div v-if="errors.name" class="hc-error">{{ errors.name }}</div>
            </div>

            <div class="hc-grid">
                <div>
                    <label class="hc-label">{{ $t('common.folder') }}</label>
                    <habit-dropdown block :items="folderItems" :value="form.folder || ''"
                        :placeholder="$t('habits.select')" @change="form.folder = $event || null" />
                </div>
                <div>
                    <label class="hc-label">{{ $t('habits.suggested_duration') }}</label>
                    <habit-dropdown block :items="durationItems" :value="form.duration || 0"
                        @change="form.duration = $event || null" />
                </div>
            </div>

            <div>
                <label class="hc-label">
                    {{ $t('habits.combo_habits') }}
                    <span class="hc-count">{{ countLabel }}</span>
                </label>
                <div v-if="!form.habits.length" class="hc-empty" :class="{ 'hc-empty--error': errors.habits }">
                    {{ $t('habits.combo_empty') }}
                </div>
                <draggable v-model="form.habits" handle=".hc-drag" class="hc-list" ghost-class="hc-ghost"
                    :animation="180">
                    <div v-for="(habit, k) in form.habits" :key="habit.key" class="hc-row"
                        :class="{ 'hc-row--alarm': showBells && habit.alarm.enabled }">
                        <div class="hc-main">
                            <span class="hc-drag"><v-icon size="18">mdi-drag-vertical</v-icon></span>
                            <habit-icon :category="habit.category" :size="34" />
                            <div class="hc-name">
                                <template v-if="habit.baseId">
                                    <div class="hc-n text-truncate">{{ habitName(habit) }}</div>
                                    <div class="hc-t">{{ habitCategoryLabel(habit.category) }} · {{ habitTypeLabel(habit.type) }}</div>
                                </template>
                                <template v-else>
                                    <input class="hc-input" :class="{ 'hc-input--error': errors[`name_${habit.key}`] }"
                                        v-model="habit.name" :placeholder="$t('habits.habit_name')" maxlength="60" />
                                    <habit-dropdown class="mt-1" :items="typeItems" :value="habit.type"
                                        @change="setType(habit, $event)" />
                                </template>
                            </div>
                            <div class="hc-controls">
                                <div v-if="habit.type === 'bool'" class="hc-goal hc-goal--bool">{{ $t('habits.yes_no') }}</div>
                                <div v-else class="hc-goal" :class="{ 'hc-goal--error': errors[`goal_${habit.key}`] }">
                                    <input v-model="habit.goal" inputmode="decimal" :placeholder="$t('habits.goal')" />
                                    <input v-if="!habit.baseId && habit.type === 'qty'" class="hc-unit-input"
                                        v-model="habit.unit" :placeholder="$t('habits.unit')" maxlength="12" />
                                    <span v-else class="hc-unit">{{ habitUnitLabel(habit.unit) }}</span>
                                </div>
                                <habit-dropdown :items="frequencyItems" :value="frequencyValue(habit.frequency)"
                                    :min-width="260" @change="habit.frequency = frequencyFromValue($event)" />
                                <button-tooltip v-if="showBells" :tooltip="habit.alarm.enabled ? $t('habits.alarm_on') : $t('habits.alarm_enable')" top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <button type="button" class="hc-bell" :class="{ 'hc-bell--on': habit.alarm.enabled }"
                                            v-bind="attrs" v-on="on" @click="habit.alarm.enabled = !habit.alarm.enabled">
                                            <v-icon size="16">{{ habit.alarm.enabled ? 'mdi-bell' : 'mdi-bell-outline' }}</v-icon>
                                        </button>
                                    </template>
                                </button-tooltip>
                                <button-tooltip :tooltip="$t('habits.remove_habit')" top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <button type="button" class="hc-del" v-bind="attrs" v-on="on" @click="form.habits.splice(k, 1)">
                                            <v-icon size="14">mdi-close</v-icon>
                                        </button>
                                    </template>
                                </button-tooltip>
                            </div>
                        </div>
                        <div v-if="showBells && habit.alarm.enabled" class="hc-al">
                            <span class="hc-al-lbl"><v-icon size="14">mdi-bell-outline</v-icon>{{ $t('habits.push_alarm') }}</span>
                            <div class="hc-al-times">
                                <span v-for="(hour, j) in habit.alarm.hours" :key="j" class="hc-al-t">
                                    <hour-clock-picker :value="hour" @change="$set(habit.alarm.hours, j, $event)" />
                                    <button v-if="habit.alarm.hours.length > 1" type="button" class="hc-al-x"
                                        @click="habit.alarm.hours.splice(j, 1)">
                                        <v-icon size="12">mdi-close</v-icon>
                                    </button>
                                </span>
                                <button v-if="habit.alarm.hours.length < 4" type="button" class="hc-al-add"
                                    @click="habit.alarm.hours.push(habit.alarm.hours[habit.alarm.hours.length - 1] || 20)">
                                    + {{ $t('habits.add_hour') }}
                                </button>
                            </div>
                            <div class="hc-prev"><b>BuilderPro</b> · {{ pushText(habit) }}</div>
                        </div>
                    </div>
                </draggable>

                <habit-dropdown :items="catalogItems" :max-height="320" :min-width="300" @change="addHabit">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="hc-add" v-bind="attrs" v-on="on">+ {{ $t('habits.add_habit') }}</button>
                    </template>
                </habit-dropdown>
                <div class="hc-hint" :class="{ 'hc-hint--warn': form.habits.length > 6 }">
                    {{ form.habits.length > 6 ? $t('habits.hint_too_many') : $t('habits.hint_recommended') }}
                </div>
                <div v-if="hasMd" class="hc-hint">{{ $t('habits.hint_md') }}</div>
            </div>

            <div>
                <label class="hc-label">{{ $t('habits.push_alarms') }}</label>
                <kimsa-segmented :items="alarmModes" :value="form.alarms.mode" @change="form.alarms.mode = $event" dense />
                <template v-if="form.alarms.mode === 'perHabit'">
                    <div class="hc-hint">{{ $t('habits.alarm_per_habit_hint') }}</div>
                    <div class="hc-rem">
                        <kimsa-toggle :value="form.alarms.closing.enabled" @change="form.alarms.closing.enabled = $event" />
                        <span class="hc-rem-lbl">{{ $t('habits.closing_alarm') }}</span>
                        <hour-clock-picker :value="form.alarms.closing.hour" :disabled="!form.alarms.closing.enabled"
                            @change="form.alarms.closing.hour = $event" />
                    </div>
                </template>
                <template v-else>
                    <div class="hc-hint">{{ $t('habits.alarm_daily_hint') }}</div>
                    <div class="hc-rem">
                        <span class="hc-rem-lbl">{{ $t('habits.alarm_hour') }}</span>
                        <hour-clock-picker :value="form.alarms.dailyHour" @change="form.alarms.dailyHour = $event" />
                    </div>
                    <div class="hc-prev"><b>BuilderPro</b> · {{ dailyPushText }}</div>
                </template>
            </div>

            <kimsa-textarea-field :label="$t('habits.notes_for_student')" :value="form.notes" @change="form.notes = $event"
                :placeholder="$t('common.write_here')" :rows="3" force-value />

            <kimsa-tags-picker @change="form.tags = $event" :default-tags="form.tags" :label="$t('exercises.tags')" force-value />
            <div class="hc-hint">{{ $t('habits.hint_goals_per_student') }}</div>
        </div>

        <template v-slot:footer>
            <dialog-actions :confirm-label="$t('common.save')" :loading="loading" @cancel="hide" @confirm="save" />
        </template>
    </base-dialog>
</template>

<script>
import { mapState } from 'vuex'
import draggable from 'vuedraggable'
import { db, Timestamp } from '@/utils/Firebase'
import {
    HABIT_CATALOG, HABIT_CATEGORIES, HABIT_CATEGORY_ICONS, HABIT_TYPES, FREQUENCY_VALUES,
    emptyCombo, habitFromCatalog, customHabit, frequencyValue, frequencyFromValue,
} from '@/utils/Habits'
import BaseDialog from '@/components/common/BaseDialog'
import DialogActions from '@/components/common/DialogActions'
import KimsaTextField from '@/components/common/inputs/KimsaTextField'
import KimsaTextareaField from '@/components/common/inputs/KimsaTextareaField'
import KimsaTagsPicker from '@/components/common/inputs/KimsaTagsPicker'
import KimsaSegmented from '@/components/common/inputs/KimsaSegmented'
import KimsaToggle from '@/components/common/inputs/KimsaToggle'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import HabitIcon from './HabitIcon.vue'
import HabitDropdown from './HabitDropdown.vue'
import HourClockPicker from './HourClockPicker.vue'
import habitsMixin from '@/mixins/habitsMixin'

export default {
    name: 'CreateHabitComboDialog',
    mixins: [habitsMixin],
    components: {
        draggable, BaseDialog, DialogActions, KimsaTextField, KimsaTextareaField, KimsaTagsPicker, KimsaSegmented,
        KimsaToggle, ButtonTooltip, HabitIcon, HabitDropdown, HourClockPicker,
    },
    props: {
        coachId: {
            type: String,
            default: null,
        },
        companyId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            comboId: null,
            form: emptyCombo(),
            errors: {},
        }
    },
    computed: {
        ...mapState(['folders']),
        title(vm = this) {
            return vm.comboId ? vm.$t('habits.edit_combo') : vm.$t('habits.create_combo')
        },
        showBells(vm = this) {
            return vm.form.alarms.mode === 'perHabit'
        },
        hasMd(vm = this) {
            return vm.form.habits.some(habit => habit.frequency?.kind === 'md')
        },
        countLabel(vm = this) {
            const n = vm.form.habits.length
            const summary = vm.habitFrequencySummary(vm.form.habits)
            return [vm.$tc('habits.habits_count', n, { n }), summary].filter(Boolean).join(' · ')
        },
        folderItems(vm = this) {
            return [
                { value: '', text: vm.$t('habits.no_folder') },
                ...vm.folders.filter(folder => folder.type === 'habitCombos')
                    .map(folder => ({ value: folder.id, text: vm.folderLabel(folder) })),
            ]
        },
        durationItems(vm = this) {
            return [
                { value: 0, text: vm.$t('habits.no_duration') },
                ...[1, 2, 3, 4, 6, 8, 12].map(n => ({ value: n, text: vm.$tc('habits.weeks', n, { n }) })),
            ]
        },
        typeItems(vm = this) {
            return HABIT_TYPES.map(type => ({ value: type, text: vm.habitTypeLabel(type) }))
        },
        frequencyItems(vm = this) {
            const items = []
            FREQUENCY_VALUES.forEach(value => {
                const frequency = frequencyFromValue(value)
                if (value === 'md_-1') items.push({ header: vm.$t('habits.competition') })
                items.push({
                    value,
                    text: vm.habitFrequencyLabel(frequency, true),
                    short: vm.habitFrequencyLabel(frequency),
                })
            })
            return items
        },
        catalogItems(vm = this) {
            const items = []
            HABIT_CATEGORIES.forEach(category => {
                items.push({ header: vm.habitCategoryLabel(category) })
                HABIT_CATALOG.filter(base => base.category === category).forEach(base => {
                    items.push({
                        value: base.id,
                        text: vm.$t(`habits.catalog.${base.id}`),
                        icon: HABIT_CATEGORY_ICONS[category],
                        used: vm.form.habits.some(habit => habit.baseId === base.id),
                    })
                })
            })
            items.push({ value: '__custom', text: vm.$t('habits.create_custom'), icon: HABIT_CATEGORY_ICONS.custom })
            return items
        },
        alarmModes(vm = this) {
            return [
                { value: 'perHabit', name: vm.$t('habits.alarm_mode_per_habit') },
                { value: 'daily', name: vm.$t('habits.alarm_mode_daily') },
            ]
        },
        dailyPushText(vm = this) {
            const names = vm.form.habits.map(habit => vm.habitName(habit))
            if (!names.length) return vm.$t('habits.push_daily_generic')
            const shown = names.slice(0, 3)
            const list = shown.length > 1 ? `${shown.slice(0, -1).join(', ')} ${vm.$t('habits.and')} ${shown[shown.length - 1]}` : shown[0]
            return vm.$tc('habits.push_daily', names.length, { n: names.length, list: `${list}${names.length > 3 ? '…' : ''}` })
        },
    },
    methods: {
        frequencyValue,
        frequencyFromValue,
        show(combo = null, folderId = null) {
            const vm = this
            vm.errors = {}
            vm.comboId = combo?.id || null
            const base = emptyCombo()
            vm.form = combo
                ? { ...base, ...JSON.parse(JSON.stringify(combo)), alarms: { ...base.alarms, ...(combo.alarms || {}) } }
                : { ...base, folder: folderId || null }
            vm.dialog = true
        },
        hide() {
            this.dialog = false
        },
        addHabit(value) {
            const vm = this
            if (value === '__custom') return vm.form.habits.push(customHabit())
            if (vm.form.habits.some(habit => habit.baseId === value)) return
            const base = HABIT_CATALOG.find(item => item.id === value)
            if (base) vm.form.habits.push(habitFromCatalog(base))
        },
        setType(habit, type) {
            habit.type = type
            if (type === 'bool') {
                habit.goal = null
                habit.unit = null
            }
            if (type === 'time') habit.unit = 'min'
            if (type === 'qty' && habit.unit === 'min') habit.unit = null
        },
        parseGoal(raw) {
            if (raw === null || raw === undefined || raw === '') return null
            const value = Number(String(raw).replace(',', '.'))
            return Number.isFinite(value) && value > 0 ? value : null
        },
        pushText(habit) {
            const vm = this
            const name = vm.habitName(habit)
            if (habit.type === 'bool') return vm.$t('habits.push_bool', { name })
            return vm.$t('habits.push_goal', { name, goal: vm.habitGoalLabel({ ...habit, goal: vm.parseGoal(habit.goal) }) })
        },
        validate() {
            const vm = this
            const errors = {}
            if (!vm.form.name?.trim()) errors.name = vm.$t('habits.error_name')
            if (!vm.form.habits.length) errors.habits = true
            vm.form.habits.forEach(habit => {
                if (!habit.baseId && !habit.name?.trim()) errors[`name_${habit.key}`] = true
                if (habit.type !== 'bool' && !vm.parseGoal(habit.goal)) errors[`goal_${habit.key}`] = true
            })
            vm.errors = errors
            return !Object.keys(errors).length
        },
        async save() {
            const vm = this
            if (!vm.validate()) return vm.toast(vm.$t('habits.error_form'), 'error')
            await vm.withLoading(async () => {
                const habits = vm.form.habits.map(habit => ({
                    ...habit,
                    name: habit.baseId ? null : habit.name.trim(),
                    goal: habit.type === 'bool' ? null : vm.parseGoal(habit.goal),
                    unit: habit.type === 'bool' ? null : (habit.unit || null),
                }))
                const payload = {
                    ...JSON.parse(JSON.stringify({ ...vm.form, habits })),
                    name: vm.form.name.trim(),
                    coachId: vm.coachId,
                    companyId: vm.companyId || null,
                    updated_at: Timestamp.now(),
                }
                delete payload.id
                if (vm.comboId) {
                    await db.collection('habitCombos').doc(vm.comboId).update(payload)
                } else {
                    await db.collection('habitCombos').add({ ...payload, used: 0, created_at: Timestamp.now() })
                }
                vm.toast(vm.$t('habits.combo_saved'))
                vm.$emit('success')
                vm.hide()
            })
        },
    },
}
</script>

<style scoped>
.hc-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.hc-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.hc-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 6px;
}

.hc-count {
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    color: var(--v-txtMuted-base);
}

.hc-error {
    margin-top: 4px;
    font-size: 12px;
    color: var(--v-error-base);
}

.hc-empty {
    padding: 16px;
    border: 1px dashed var(--v-outline2-base);
    border-radius: 10px;
    text-align: center;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.hc-empty--error {
    border-color: var(--v-error-base);
    color: var(--v-error-base);
}

.hc-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.hc-row {
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    background: var(--v-surface-base);
    padding: 8px 10px 8px 4px;
}

.hc-row--alarm {
    border-color: rgba(51, 153, 153, .45);
}

.hc-main {
    display: flex;
    align-items: center;
    gap: 8px;
}

.hc-drag {
    display: flex;
    align-items: center;
    width: 20px;
    color: var(--v-txtMuted-base);
    cursor: grab;
}

.hc-drag .v-icon {
    color: inherit !important;
}

.hc-ghost {
    opacity: .4;
    border-style: dashed;
    border-color: var(--v-accent-base);
}

.hc-name {
    flex: 1;
    min-width: 0;
}

.hc-n {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.hc-t {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.hc-input,
.hc-goal input {
    width: 100%;
    height: 34px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
    font-size: 13px;
    outline: none;
}

.hc-input:focus,
.hc-goal input:focus {
    border-color: var(--v-accent-base);
}

.hc-input--error {
    border-color: var(--v-error-base) !important;
}

.hc-controls {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.hc-goal {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 150px;
}

.hc-goal input {
    width: 70px;
    text-align: right;
}

.hc-goal--error input {
    border-color: var(--v-error-base);
}

.hc-goal--bool {
    height: 34px;
    justify-content: center;
    border-radius: 8px;
    border: 1px dashed var(--v-outline-base);
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.hc-unit {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
}

.hc-goal .hc-unit-input {
    width: 70px;
    text-align: left;
}

.hc-bell,
.hc-del {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    color: var(--v-txtMuted-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all .15s;
}

.hc-bell .v-icon,
.hc-del .v-icon {
    color: inherit !important;
}

.hc-bell--on {
    color: var(--v-accent-base);
    border-color: rgba(51, 153, 153, .45);
    background: rgba(51, 153, 153, .1);
}

.hc-del:hover {
    color: var(--v-error-base);
    border-color: rgba(232, 92, 92, .4);
}

.hc-al {
    margin: 8px 0 2px 28px;
    padding-top: 8px;
    border-top: 1px solid var(--v-outline-base);
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.hc-al-lbl {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 600;
    color: var(--v-accent-base);
}

.hc-al-lbl .v-icon {
    color: inherit !important;
}

.hc-al-times {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.hc-al-t {
    display: inline-flex;
    align-items: center;
    gap: 2px;
}

.hc-al-x {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: none;
    background: none;
    color: var(--v-txtMuted-base);
    cursor: pointer;
}

.hc-al-x .v-icon {
    color: inherit !important;
}

.hc-al-add {
    height: 32px;
    padding: 0 12px;
    border-radius: 20px;
    border: 1px dashed var(--v-outline2-base);
    background: none;
    color: var(--v-accent-base);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
}

.hc-prev {
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    background: var(--v-surface2-base);
    border-radius: 8px;
    padding: 8px 10px;
    margin-top: 8px;
}

.hc-add {
    margin-top: 8px;
    height: 36px;
    padding: 0 14px;
    border-radius: 8px;
    border: 1px dashed rgba(51, 153, 153, .5);
    background: none;
    color: var(--v-accent-base);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .04em;
    cursor: pointer;
}

.hc-hint {
    margin-top: 6px;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.hc-hint--warn {
    color: #E8C000;
}

.hc-rem {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
}

.hc-rem-lbl {
    flex: 1;
    font-size: 12px;
    color: var(--v-txtSecondary-base);
}

@media (max-width: 700px) {
    .hc-grid {
        grid-template-columns: 1fr;
    }

    .hc-main {
        flex-wrap: wrap;
    }

    .hc-name {
        flex: 1 1 calc(100% - 70px);
    }

    .hc-controls {
        width: 100%;
        padding-left: 28px;
        flex-wrap: wrap;
    }

    .hc-al {
        margin-left: 0;
    }
}
</style>
